import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { StatCard } from '@/components/StatCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DollarSign, Clock, AlertCircle, TrendingUp, ArrowRight, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatCurrency, formatDate, calculateProjectStats, getPaymentStatus } from '@/utils/helpers';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const Dashboard = () => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { user, isAdmin } = useAuth();
  const [customPaymentTypes, setCustomPaymentTypes] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');

  // Load custom payment types
  useEffect(() => {
    const stored = localStorage.getItem('customPaymentTypes');
    if (stored) {
      setCustomPaymentTypes(JSON.parse(stored));
    }
  }, []);

  const userProjects = isAdmin ? projects : projects.filter((p) => p.createdBy === user?.id);
  
  // Get unique companies from projects
  const companies = [...new Set(userProjects.map((p) => p.clientName))].sort();
  
  // Filter projects by selected company
  const filteredProjects = selectedCompany === 'all' 
    ? userProjects 
    : userProjects.filter((p) => p.clientName === selectedCompany);
  
  const stats = calculateProjectStats(filteredProjects);

  const paymentTypes = filteredProjects.reduce((acc, project) => {
    acc[project.paymentType] = (acc[project.paymentType] || 0) + 1;
    return acc;
  }, {});

  // Get all payment type labels including custom ones
  const getPaymentTypeLabels = () => {
    const defaultLabels = {
      outsourced: 'Outsourced',
      recurring: 'Recurring',
      'one-time': 'One-time',
    };
    
    customPaymentTypes.forEach(ct => {
      defaultLabels[ct.value] = ct.label;
    });
    
    return defaultLabels;
  };

  const paymentTypeLabels = getPaymentTypeLabels();
  const allPaymentTypes = Object.keys(paymentTypes);
  const pieLabels = allPaymentTypes.map(type => paymentTypeLabels[type] || type);
  
  // Generate colors dynamically
  const generateColors = (count) => {
    const baseColors = [
      'hsl(217, 91%, 45%)',
      'hsl(199, 89%, 48%)',
      'hsl(142, 71%, 45%)',
      'hsl(346, 77%, 50%)',
      'hsl(26, 83%, 52%)',
      'hsl(280, 65%, 60%)',
      'hsl(160, 60%, 45%)',
    ];
    
    if (count <= baseColors.length) {
      return baseColors.slice(0, count);
    }
    
    // Generate additional colors if needed
    const additionalColors = [];
    for (let i = baseColors.length; i < count; i++) {
      const hue = (i * 360 / count) % 360;
      additionalColors.push(`hsl(${hue}, 70%, 50%)`);
    }
    
    return [...baseColors, ...additionalColors];
  };

  const pieData = {
    labels: pieLabels,
    datasets: [
      {
        data: allPaymentTypes.map(type => paymentTypes[type]),
        backgroundColor: generateColors(allPaymentTypes.length),
        borderWidth: 2,
        borderColor: 'hsl(0, 0%, 100%)',
      },
    ],
  };

  const clientOutstanding = {};
  filteredProjects.forEach((project) => {
    let outstanding = 0;
    project.paymentSchedule.forEach((payment) => {
      const status = getPaymentStatus(payment.dueDate, payment.status);
      if (status !== 'paid') outstanding += payment.amount;
    });
    if (outstanding > 0) {
      clientOutstanding[project.clientName] = (clientOutstanding[project.clientName] || 0) + outstanding;
    }
  });

  const topClients = Object.entries(clientOutstanding).sort((a, b) => b[1] - a[1]).slice(0, 5);

  // Generate bar chart data based on selection
  const getBarChartData = () => {
    if (selectedCompany === 'all') {
      return {
        labels: topClients.map((c) => c[0]),
        data: topClients.map((c) => c[1]),
        label: 'Outstanding Amount'
      };
    } else {
      // For specific company, show outstanding by project
      const projectOutstanding = filteredProjects
        .map((p) => {
          let outstanding = 0;
          p.paymentSchedule.forEach((payment) => {
            const status = getPaymentStatus(payment.dueDate, payment.status);
            if (status !== 'paid') outstanding += payment.amount;
          });
          return { name: p.projectName, outstanding };
        })
        .filter(p => p.outstanding > 0)
        .sort((a, b) => b.outstanding - a.outstanding);
      
      return {
        labels: projectOutstanding.map((p) => p.name),
        data: projectOutstanding.map((p) => p.outstanding),
        label: 'Project Outstanding'
      };
    }
  };

  const barChartData = getBarChartData();

  const barData = {
    labels: barChartData.labels,
    datasets: [
      {
        label: barChartData.label,
        data: barChartData.data,
        backgroundColor: 'hsl(217, 91%, 45%)',
        borderRadius: 6,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (context) => formatCurrency(context.parsed.y) } },
    },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (value) => formatCurrency(value) } },
    },
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Welcome back, {user?.name}! Here's your payment overview.
          </p>
        </div>
        <Link to="/projects/new">
          <Button size="lg" className="gap-2 w-full sm:w-auto">
            <TrendingUp className="h-4 w-4" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Company Filter */}
      {companies.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex items-center gap-2 min-w-fit">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter by Company:</span>
              </div>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="w-full sm:w-[250px]">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company} value={company}>
                      {company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCompany !== 'all' && (
                <Badge variant="outline" className="gap-1">
                  {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Received"
          value={formatCurrency(stats.totalReceived)}
          icon={DollarSign}
          trend="up"
          trendValue="+12% from last month"
          onClick={() => navigate('/projects')}
        />
        <StatCard
          title="Pending Payments"
          value={formatCurrency(stats.totalPending)}
          icon={Clock}
          description={`${stats.upcomingPayments.length} upcoming`}
          onClick={() => navigate('/projects')}
        />
        <StatCard
          title="Overdue Payments"
          value={formatCurrency(stats.totalOverdue)}
          icon={AlertCircle}
          trend="down"
          trendValue={`${stats.overduePayments.length} payments`}
          onClick={() => navigate('/projects')}
        />
        <StatCard
          title="Active Projects"
          value={filteredProjects.filter((p) => p.status === 'active').length}
          icon={TrendingUp}
          description="Currently in progress"
          onClick={() => navigate('/projects')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/projects')}
        >
          <CardHeader>
            <CardTitle>Payment Type Distribution</CardTitle>
            <CardDescription>Breakdown of project types</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full h-[300px] sm:h-[350px] flex items-center justify-center">
              {Object.keys(paymentTypes).length > 0 ? (
                <Pie data={pieData} options={{ maintainAspectRatio: false }} />
              ) : (
                <p className="text-muted-foreground text-center">No projects yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/projects')}
        >
          <CardHeader>
            <CardTitle>
              {selectedCompany === 'all' ? 'Top 5 Clients by Outstanding' : 'Outstanding Breakdown'}
            </CardTitle>
            <CardDescription>
              {selectedCompany === 'all' ? 'Clients with pending payments' : `${selectedCompany}'s pending payments`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="w-full h-[300px] sm:h-[350px]">
              {barData.labels.length > 0 ? (
                <Bar data={barData} options={barOptions} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No outstanding payments</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent & Upcoming Payments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Upcoming */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle>Upcoming Payments</CardTitle>
                <CardDescription>Next 30 days</CardDescription>
              </div>
              <Link to="/reminders">
                <Button variant="ghost" size="sm" className="gap-1">
                  View All
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {stats.upcomingPayments.length > 0 ? (
              <div className="space-y-4">
                {stats.upcomingPayments.slice(0, 5).map((payment) => (
                  <div
                    key={payment.id}
                    onClick={() => navigate(`/projects/${payment.project.id}#payment-${payment.id}`)}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors gap-2 cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{payment.paymentName}</p>
                      <p className="text-sm text-muted-foreground truncate">{payment.project.clientName}</p>
                      <p className="text-xs text-muted-foreground mt-1">Due: {formatDate(payment.dueDate)}</p>
                    </div>
                    <div className="text-right ml-0 sm:ml-4">
                      <p className="font-semibold">{formatCurrency(payment.amount)}</p>
                      <StatusBadge status={getPaymentStatus(payment.dueDate, payment.status)} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No upcoming payments</p>
            )}
          </CardContent>
        </Card>

        {/* Overdue */}
        <Card className="border-destructive/50">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-destructive">Overdue Payments</CardTitle>
                <CardDescription>Requires immediate attention</CardDescription>
              </div>
              <Badge variant="destructive">{stats.overduePayments.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {stats.overduePayments.length > 0 ? (
              <div className="space-y-4">
                {stats.overduePayments.slice(0, 5).map((payment) => (
                  <div
                    key={payment.id}
                    onClick={() => navigate(`/projects/${payment.project.id}#payment-${payment.id}`)}
                    className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 rounded-lg border border-destructive/20 bg-destructive/5 hover:bg-destructive/10 transition-colors gap-2 cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{payment.paymentName}</p>
                      <p className="text-sm text-muted-foreground truncate">{payment.project.clientName}</p>
                      <p className="text-xs text-destructive mt-1">
                        Overdue: {formatDate(payment.dueDate)}
                      </p>
                    </div>
                    <div className="text-right ml-0 sm:ml-4">
                      <p className="font-semibold">{formatCurrency(payment.amount)}</p>
                      <StatusBadge status="overdue" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No overdue payments</p>
                <p className="text-xs text-success mt-2">All payments are on track!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
